import { attr, customElement, FASTElement, observable } from '@microsoft/fast-element';
import { SellerTileTemplate as template } from './seller-tile.template';
import { SellerTileStyles as styles } from './seller-tile.styles';
import { DashboardMetric, Project } from '../../../../types';
import {
  ICONS,
  PRODUCT_TYPE,
  PROJECT_OPERATIONAL_STATUS,
  VPPA_RFQ_STATUS,
} from '../../../../utils';
import {
  formatCodDate,
  getFillColorFromProjectStatus,
  getFormattedNumber,
} from '../../../../utils/formatting';
import { updateArray } from '../../../../utils/dataserver';
import { INegotiationService, IVppaService } from '../../../../services';
import { Message, SocketSubscription } from '@genesislcap/foundation-comms';
import { IIndividualProjectPageService } from '../../../../services/individual-project-page.service';
import { ICompanyService } from '../../../../services/company.service';
import { COMPANY_ROLE } from '../../../mf/companys/edit';
import { getTechnologyIconName } from "../../../../styles";

const name = 'seller-tile';

export interface SellerTileValueItem {
  value: any;
  icon: { name: ICONS; slot: string; width?: string; height?: string; fill?: string };
}

export interface SellerTileValue {
  title: string;
  tooltip?: string;
  items: Array<SellerTileValueItem>;
}

interface SellerTileObject {
  title: string;
  subTitle: string;
  image: any;
  inventoryProgress: Array<SellerTileValue>;
  valuesList: Array<SellerTileValue>;
}

export enum Labels {
  TOTAL_NAMEPLATE_CAP = 'Total Nameplate Capacity (MW)',
  NAMEPLATE_CAP_MW = 'Nameplate Capacity (MW)',
  TOTAL_AVAILABLE_CAPACITY = 'Total Available Capacity (MW)',
  TOTAL_EXECUTED_CAPACITY = 'Total Executed Capacity (MW)',
  TOTAL_TRADABLE_VOLUME = 'Total Tradable Capacity (MW)',
  AVAILABLE_CAPACITY_MW = 'Available Capacity',
  TRADABLE_CAPACITY_MW = 'Tradable Capacity',
  EXECUTED_CAPACITY_MW = 'Executed Capacity',
  TTL_LISTED_CONTRACTS = 'Total Offered Contracts',
  TTL_EXECUTED_CONTRACTS = 'Total Executed Contracts',
  LOCATION = 'Location',
  ENERGY_TYPE = 'Energy Type',
  COD = 'COD',
  GUARANTEED_COD = 'Guaranteed COD',
  PRODUCT_TYPES = 'Product Types',
}

const ICON_SIZE = { width: '24px', height: '18px' };

@customElement({
  name,
  template,
  styles,
})
export class SellerTile extends FASTElement {
  @IVppaService vppaService: IVppaService;
  @INegotiationService negotiationService: INegotiationService;
  @IIndividualProjectPageService individualProjectPageService: IIndividualProjectPageService;
  @ICompanyService companyService: ICompanyService;

  @observable data: SellerTileObject;
  private nameplate: number;
  private tradableCapacity: number;
  @observable availableCapacity: number;
  @observable executedVolume: number;

  @attr @observable project: Project = null;

  private projectsNameplate: number;
  @observable projectsExecutedVolume: number;
  private projectsTradableCapacity: number;
  @observable projectsAvailableCapacity: number;
  @observable projectsList: Array<Project> = [];

  @observable allProjectsSummary: Array<SellerTileValue> = [];
  @observable allProjectsContracts: Array<SellerTileValue> = [];

  @observable projectSliderValue: string = '0';
  @observable vppaSubscription: SocketSubscription;
  private vppaList: Array<any> = [];

  private dashboardMetrics: Array<DashboardMetric> = [];

  metricsSubscription: SocketSubscription;

  connectedCallback() {
    super.connectedCallback();
    this.allProjectsSummary = [];
    this.allProjectsContracts = [];
    this.getListedContracts();
    this.getDashboardMetrics();
  }

  public setProjectsList(projectsList: Array<Project>) {
    if (projectsList && this.allProjectsSummary) {
      this.showAllProjectsSummary(projectsList);
    }
  }

  showAllProjectsSummary(value: Array<Project>) {
    this.projectsList = value;

    this.projectsNameplate = 0;
    this.projectSliderValue = '0';
    this.projectsExecutedVolume = 0;
    this.projectsTradableCapacity = 0;
    this.projectsAvailableCapacity = 0;

    if (this.dashboardMetrics && this.dashboardMetrics.length > 0) {
      this.dashboardMetrics?.forEach((project) => {
        this.projectsExecutedVolume += project.EXECUTED_VOLUME ? project.EXECUTED_VOLUME : 0;
      });
    }

    if (value && value.length > 0) {
      value?.forEach((project) => {
        this.projectsNameplate += project.MEGAWATT_AC;
        this.projectsAvailableCapacity += project.AVAILABLE_CAPACITY_AC ? project.AVAILABLE_CAPACITY_AC : 0;
      });
    }

    this.projectSliderValue = this.getScrollbarValue(
        this.projectsExecutedVolume,
        this.projectsAvailableCapacity,
    );

    this.projectsTradableCapacity = this.projectsAvailableCapacity - this.projectsExecutedVolume;

    this.fillValuesForDisplayingVolumes();
    this.fillValuesForDisplayingContracts();
  }

  private fillValuesForDisplayingVolumes() {
    if (this.allProjectsSummary.length > 0) {
      this.allProjectsSummary.splice(0, this.allProjectsSummary.length);
    }

    this.allProjectsSummary.push({
      title: Labels.TOTAL_AVAILABLE_CAPACITY,
      tooltip: 'The maximum rated output (in MW) across all Projects that the seller is willing or able to contract as entered in the Edit Project modal. ',
      items: [
        {
          value: this.projectsAvailableCapacity === 0 ? 0 : getFormattedNumber(this.projectsAvailableCapacity, '', 2),
          icon: null,
        },
      ],
    });

    this.allProjectsSummary.push({
      title: Labels.TOTAL_TRADABLE_VOLUME,
      tooltip: 'The total capacity (in MW) across all Projects that can be traded on the Platform. It is calculated as Available Capacity minus Executed Capacity.',
      items: [
        {
          value: this.projectsTradableCapacity === 0 ? 0 : getFormattedNumber(this.projectsTradableCapacity, '', 2),
          icon: null,
        },
      ],
    });

    this.allProjectsSummary.push({
      title: Labels.TOTAL_EXECUTED_CAPACITY,
      tooltip: 'The total executed capacity (in MW) across all Projects.',
      items: [
        {
          value:
              this.projectsExecutedVolume === 0
                  ? 0
                  : getFormattedNumber(this.projectsExecutedVolume, '', 2),
          icon: null,
        },
      ],
    });
  }

  private getListedContracts() {
    let resultList = [];
    this.vppaSubscription = this.vppaService
        .getMyVppas(
            `CASCADE_COMPANY_ID == '${
                this.companyService.getCurrentCompany().CASCADE_COMPANY_ID
            }' && (VPPA_STATUS == '${VPPA_RFQ_STATUS.ACTIVE}' || VPPA_STATUS == '${
                VPPA_RFQ_STATUS.EXISTING_INTEREST
            }' || VPPA_STATUS == '${VPPA_RFQ_STATUS.TRADE_PENDING}' || VPPA_STATUS == '${
                VPPA_RFQ_STATUS.EXECUTED
            }') `,
        )
        .subscribe({
          next: (streamMessage: Message) => {
            resultList = streamMessage.ROW ?? null;
            if (resultList) {
              this.vppaList = updateArray(resultList, this.vppaList);
            }
            if (this.projectsList && this.projectsList.length > 0) {
              this.showAllProjectsSummary(this.projectsList);
            }

            this.projectChanged(null, this.project);
            if (this.project) {
              this.data.valuesList.shift();
              this.data.valuesList.unshift({
                title: Labels.TTL_LISTED_CONTRACTS,
                items: [
                  {
                    value: this.vppaList?.filter(
                        (vppa) =>
                            vppa.VPPA_STATUS !== VPPA_RFQ_STATUS.EXECUTED &&
                            vppa.PROJECT_ID === this.project?.PROJECT_ID,
                    ).length,
                    icon: null,
                  },
                ],
              });
            }
          },
          error: (error: any) => {},
        });
  }

  private getDashboardMetrics() {
    let resultList = [];
    let criteriaMatch = '';
    if (this.companyService.getCurrentCompany().COMPANY_ROLE !== COMPANY_ROLE.CASCADE_ADMIN) {
      criteriaMatch = `CASCADE_COMPANY_ID == '${
          this.companyService.getCurrentCompany().CASCADE_COMPANY_ID
      }' `;
    }
    this.metricsSubscription = this.vppaService.getDashboardMetrics(criteriaMatch).subscribe({
      next: (streamMessage: Message) => {
        resultList = streamMessage.ROW ?? null;
        if (resultList) {
          this.dashboardMetrics = updateArray(resultList, this.dashboardMetrics);
        }
        if (this.projectsList && this.projectsList.length > 0) {
          this.showAllProjectsSummary(this.projectsList);
        }

        this.projectChanged(null, this.project);
      },
      error: (error: any) => {},
    });
  }

  private fillValuesForDisplayingContracts() {
    if (this.allProjectsContracts.length > 0) {
      this.allProjectsContracts.splice(0, this.allProjectsContracts.length);
    }

    this.allProjectsContracts.push({
      title: Labels.TOTAL_NAMEPLATE_CAP,
      items: [
        {
          value: this.projectsNameplate === 0 ? 0 : getFormattedNumber(this.projectsNameplate),
          icon: null,
        },
      ],
    });

    this.allProjectsContracts.push({
      title: Labels.TTL_LISTED_CONTRACTS,
      items: [
        {
          value: this.vppaList.filter((vppa) => vppa.VPPA_STATUS !== VPPA_RFQ_STATUS.EXECUTED)
              .length,
          icon: null,
        },
      ],
    });

    this.allProjectsContracts.push({
      title: Labels.TTL_EXECUTED_CONTRACTS,
      items: [
        {
          value: this.vppaList.filter((vppa) => vppa.VPPA_STATUS === VPPA_RFQ_STATUS.EXECUTED)
              .length,
          icon: null,
        },
      ],
    });
  }

  public projectChanged(oldValue, newValue: Project) {
    const valueList: Array<SellerTileValue> = [];
    const inventoryProgress: Array<SellerTileValue> = [];

    let metric = this.dashboardMetrics?.find((x) => x?.PROJECT_ID === newValue?.PROJECT_ID);

    let iconName = getTechnologyIconName(newValue?.TECHNOLOGY);
    this.nameplate = newValue?.MEGAWATT_AC;
    this.executedVolume = metric?.EXECUTED_VOLUME || 0;
    this.availableCapacity = newValue?.AVAILABLE_CAPACITY_AC;
    this.tradableCapacity = newValue?.AVAILABLE_CAPACITY_AC - this.executedVolume;

    valueList.push({
      title: Labels.NAMEPLATE_CAP_MW,
      items: [
        {
          value: this.nameplate === 0 ? 0 : getFormattedNumber(this.nameplate),
          icon: null,
        },
      ],
    });

    inventoryProgress.push({
      title: Labels.AVAILABLE_CAPACITY_MW,
      tooltip: 'The maximum rated output (in MW) that the seller is willing or able to contract as entered in the Edit Project modal.',
      items: [
        {
          value: this.availableCapacity === 0 ? 0 : getFormattedNumber(this.availableCapacity, '', 2),
          icon: null,
        },
      ],
    });

    inventoryProgress.push({
      title: Labels.TRADABLE_CAPACITY_MW,
      tooltip: 'The capacity (in MW) available to trade for the selected Project. It is calculated as Available Capacity minus Executed Capacity.',
      items: [
        {
          value: this.tradableCapacity === 0 ? 0 : getFormattedNumber(this.tradableCapacity, '', 2),
          icon: null,
        },
      ],
    });

    inventoryProgress.push({
      title: Labels.EXECUTED_CAPACITY_MW,
      tooltip: 'The executed capacity (in MW) for the selected Project.',
      items: [
        {
          value: this.executedVolume === 0 ? 0 : getFormattedNumber(this.executedVolume, '', 2),
          icon: null,
        },
      ],
    });

    valueList.push({
      title: Labels.TTL_LISTED_CONTRACTS,
      items: [
        {
          value: this.vppaList?.filter(
              (vppa) =>
                  vppa.VPPA_STATUS !== VPPA_RFQ_STATUS.EXECUTED &&
                  vppa.PROJECT_ID === newValue?.PROJECT_ID,
          ).length,
          icon: null,
        },
      ],
    });

    valueList.push({
      title: Labels.LOCATION,
      items: [
        {
          value: newValue?.STATE_ABBREVIATION,
          icon: null,
        },
      ],
    });

    valueList.push({
      title: Labels.ENERGY_TYPE,
      items: [
        {
          value: newValue?.TECHNOLOGY,
          icon: {
            name: iconName,
            slot: 'start',
            fill: getFillColorFromProjectStatus(newValue?.PROJECT_OPERATIONAL_STATUS),
            ...ICON_SIZE,
          },
        },
      ],
    });

    valueList.push({
      title:
          newValue?.PROJECT_OPERATIONAL_STATUS === PROJECT_OPERATIONAL_STATUS.OPERATIONAL
              ? Labels.COD
              : Labels.GUARANTEED_COD,
      items: [
        {
          value: formatCodDate(
              newValue?.PROJECT_OPERATIONAL_STATUS === PROJECT_OPERATIONAL_STATUS.OPERATIONAL
                  ? newValue?.COD
                  : newValue?.GUARANTEED_COD,
          ),
          icon: null,
        },
      ],
    });

    valueList.push({
      title: Labels.PRODUCT_TYPES,
      items: [
        {
          value: 'Virtual PPA',
          icon: this.getIconForProductType(PRODUCT_TYPE.VIRTUAL_PPA, newValue),
        },
        {
          value: 'Physical PPA',
          icon: this.getIconForProductType(PRODUCT_TYPE.PHYSICAL_PPA, newValue),
        },
        {
          value: 'RECs Only',
          icon: this.getIconForProductType(PRODUCT_TYPE.RECS, newValue),
        },
      ],
    });

    this.data = {
      title: newValue?.NAME,
      subTitle: newValue?.OWNER,
      image: '',
      inventoryProgress: inventoryProgress,
      valuesList: valueList,
    };
  }

  private getIconForProductType(productType: string, project: Project) {
    const currentProjectVppas = this.vppaList
        ? this.vppaList.filter((vppa) => vppa?.PROJECT_ID == project?.PROJECT_ID)
        : [];
    const isProductTypeFound = currentProjectVppas?.some(
        (vppa) => vppa.PRODUCT_TYPE === productType,
    );

    return {
      name: isProductTypeFound ? ICONS.GREEN_CHECK : ICONS.CIRCLED_ERROR,
      slot: 'start',
      ...ICON_SIZE,
    };
  }

  public openIndividualProjectPage() {
    this.individualProjectPageService.openIndividualProjectPage(this.project.PROJECT_ID);
  }

  public getScrollbarValue(executedVolume: number, availableVolume: number): string {
    let tempScrollValue =
        availableVolume > 0
            ? getFormattedNumber((executedVolume / availableVolume) * 100, '0', 1)
            : '0';
    //Used just in case values are very close together but not exactly equal
    return tempScrollValue === '100' && executedVolume != availableVolume ? '99.9' : tempScrollValue;
  }

  public getExecutedFractionText(executedVolume: number, availableVolume: number): string {
    return ` ${getFormattedNumber(executedVolume, '', 2)} MW (Executed) / ${getFormattedNumber(availableVolume, '', 2)} MW (Available)`;
  }
}